import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from '../components/ui/sonner';
import {
  Loader2, AlertCircle, Building2, Home as HomeIcon, User, MessageSquare,
} from 'lucide-react';
import DealMessagesPanel from './DealMessagesPanel';
import BorrowerProgress from './shared/BorrowerProgress';
import { BRAND } from './kit/tokens';
import { addressLine } from './shared/address';
import { formatPhone, telHref } from './shared/phone';
import { publicLinkError } from './errors';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const publicApi = axios.create({ baseURL: `${BACKEND_URL}/api` });

// property_address may be a legacy string or an Address object — render both.
const addr = addressLine;

export default function BorrowerDealPortal() {
  const { token } = useParams();
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    publicApi.get(`/borrower/deal/${token}`)
      .then(({ data }) => setSummary(data))
      .catch((e) => setError(publicLinkError(e, 'Invalid or expired link')))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center text-slate-500 gap-2">
      <Loader2 className="animate-spin" size={16} /> Loading your deal…
    </div>
  );
  if (error) return (
    <div className="min-h-screen flex items-center justify-center p-8">
      <div className="max-w-md text-center">
        <AlertCircle size={40} className="mx-auto text-red-500" />
        <h1 className="mt-4 font-serif text-2xl font-bold text-slate-900">Link unavailable</h1>
        <p className="mt-2 text-sm text-slate-600">{error}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="borrower-deal-portal">
      <Toaster />
      <header className="border-b bg-white sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-full flex items-center justify-center bg-brand-navy">
            <Building2 size={18} color="white" />
          </div>
          <div className="flex-1">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Your home purchase</div>
            <div className="font-serif text-lg font-bold text-brand-navy">
              Hi {summary.borrower_name || 'there'} 👋
            </div>
          </div>
          <span className="text-[11px] font-bold uppercase px-3 py-1 rounded" style={{ background: '#dcfce7', color: '#166534' }}>
            Live
          </span>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 md:px-8 py-6 space-y-5">
        {/* Property + team card */}
        <div className="rounded-2xl p-6 text-white grid md:grid-cols-3 gap-4" style={{ background: `linear-gradient(160deg, ${BRAND.navyDeep} 0%, ${BRAND.navy} 60%, ${BRAND.navyLight} 100%)` }} data-testid="deal-summary">
          <div>
            <div className="text-xs uppercase tracking-wider text-brand-accent">Property</div>
            <div className="font-serif text-lg font-semibold mt-0.5">{addr(summary.property_address) || 'Being finalized'}</div>
          </div>
          <TeamCard icon={<HomeIcon size={14} />} label="Your Realtor" name={summary.agent_name || 'Assigned soon'} active={summary.has_re_side} />
          <TeamCard icon={<User size={14} />} label="Your Loan Officer" name={summary.lo_contact?.name || summary.lo_name || 'Assigned soon'} active={summary.has_loan_side} contact={summary.lo_contact} />
        </div>

        {summary.progress && (
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100" data-testid="deal-progress">
            <div className="font-serif font-semibold text-lg mb-4 text-brand-navy">Where your loan stands</div>
            <BorrowerProgress progress={summary.progress} />
          </div>
        )}

        {/* Two-way messaging */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <MessageSquare size={14} className="text-brand-navy" />
            <h2 className="font-serif text-lg font-bold text-brand-navy">Chat with your team</h2>
          </div>
          <p className="text-xs text-slate-500 mb-3">
            One thread. Your realtor and loan officer see every message — no more juggling texts and emails.
          </p>
          <DealMessagesPanel
            apiClient={publicApi}
            shareToken={token}
            currentUserRole="borrower"
            navy={BRAND.navy}
            green={BRAND.accent}
          />
        </div>
      </div>
    </div>
  );
}

function TeamCard({ icon, label, name, active, contact }) {
  return (
    <div className="rounded-xl p-3 flex items-center gap-2" style={{ background: 'rgba(255,255,255,0.08)' }}>
      <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.15)' }}>
        {icon}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-wider text-white/60">{label}</div>
        <div className="text-sm font-semibold truncate">{name}</div>
        {contact && (
          <div className="text-xs text-white/80 space-y-0.5 mt-0.5" data-testid="lo-contact">
            {contact.phone && <div className="truncate"><a href={telHref(contact.phone)} className="hover:underline">{formatPhone(contact.phone)}</a></div>}
            {contact.email && <div className="truncate"><a href={`mailto:${contact.email}`} className="hover:underline">{contact.email}</a></div>}
            {contact.nmls && <div>NMLS #{contact.nmls}</div>}
          </div>
        )}
      </div>
      <span className={`w-2 h-2 rounded-full ${active ? 'bg-emerald-400' : 'bg-slate-500'}`} title={active ? 'Active' : 'Pending'} />
    </div>
  );
}
